import React, { useState, useEffect } from 'react';
import { 
  Building2, Users, MessageSquare, TrendingUp, DollarSign, 
  AlertTriangle, CheckCircle2, XCircle, Activity,
  BarChart3
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ⭐ Função auxiliar para converter valores com segurança
const toNumber = (value) => {
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(toNumber(value));
};

function MasterDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMetrics();
  }, []);

  const loadMetrics = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/master-admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMetrics(res.data);
      setLoading(false);
    } catch (err) {
      console.error('Erro ao carregar métricas:', err);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="text-center text-gray-500 py-12">
        <AlertTriangle size={48} className="mx-auto mb-4 text-orange-500" />
        <p className="text-lg font-semibold">Erro ao carregar dados</p>
        <p className="text-sm">Verifique sua conexão e tente novamente</p>
      </div>
    );
  }

  // ⭐ Converter todos os valores para número com segurança
  const tenantStats = {
    total: toNumber(metrics.tenantStats?.total),
    active: toNumber(metrics.tenantStats?.active),
    suspended: toNumber(metrics.tenantStats?.suspended),
    cancelled: toNumber(metrics.tenantStats?.cancelled),
    new_this_month: toNumber(metrics.tenantStats?.new_this_month)
  };

  const revenueStats = {
    mrr: toNumber(metrics.revenueStats?.mrr),
    basic_count: toNumber(metrics.revenueStats?.basic_count),
    professional_count: toNumber(metrics.revenueStats?.professional_count),
    enterprise_count: toNumber(metrics.revenueStats?.enterprise_count)
  };

  const userStats = {
    total_users: toNumber(metrics.userStats?.total_users)
  };

  const messageStats = {
    total_messages: toNumber(metrics.messageStats?.total_messages)
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard Master</h1>
        <p className="text-gray-500">Visão geral do sistema Soulidari</p>
      </div>

      {/* Cards Principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total de Empresas */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
              <Building2 size={24} />
            </div>
            {tenantStats.new_this_month > 0 && (
              <span className="text-xs font-medium text-green-600 flex items-center gap-1">
                <TrendingUp size={12} />
                +{tenantStats.new_this_month} este mês
              </span>
            )}
          </div>
          <p className="text-3xl font-bold text-gray-900">{tenantStats.total}</p>
          <p className="text-sm text-gray-500 mt-1">Empresas no sistema</p>
          <div className="flex gap-2 mt-3 text-xs">
            <span className="px-2 py-1 bg-green-50 text-green-700 rounded-full">
              {tenantStats.active} ativas
            </span>
            <span className="px-2 py-1 bg-red-50 text-red-700 rounded-full">
              {tenantStats.suspended} suspensas
            </span>
          </div>
        </div>

        {/* Receita Mensal (MRR) */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-50 text-green-600 rounded-xl">
              <DollarSign size={24} />
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {formatCurrency(revenueStats.mrr)}
          </p>
          <p className="text-sm text-gray-500 mt-1">Receita Mensal Recorrente</p>
          <div className="flex gap-2 mt-3 text-xs flex-wrap">
            <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full">
              {revenueStats.basic_count} Básico
            </span>
            <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded-full">
              {revenueStats.professional_count} Pro
            </span>
            <span className="px-2 py-1 bg-orange-50 text-orange-700 rounded-full">
              {revenueStats.enterprise_count} Enterprise
            </span>
          </div>
        </div>

        {/* Total de Usuários */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-50 text-purple-600 rounded-xl">
              <Users size={24} />
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {userStats.total_users.toLocaleString('pt-BR')}
          </p>
          <p className="text-sm text-gray-500 mt-1">Usuários ativos no sistema</p>
        </div>

        {/* Mensagens (30 dias) */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-orange-50 text-orange-600 rounded-xl">
              <MessageSquare size={24} />
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {messageStats.total_messages.toLocaleString('pt-BR')}
          </p>
          <p className="text-sm text-gray-500 mt-1">Mensagens (últimos 30 dias)</p>
        </div>
      </div>

      {/* Alertas e Ações Rápidas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Alertas de Limite */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-50">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <AlertTriangle size={20} className="text-orange-500" />
              Alertas de Limite
            </h2>
          </div>
          <div className="divide-y divide-gray-50 max-h-80 overflow-y-auto">
            {metrics.limitAlerts && metrics.limitAlerts.length > 0 ? (
              metrics.limitAlerts.slice(0, 5).map((alert, idx) => (
                <div key={idx} className="p-4 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{alert.name}</p>
                    <p className="text-sm text-gray-500">
                      {toNumber(alert.current_users)} / {toNumber(alert.max_users)} usuários
                    </p>
                  </div>
                  <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-bold ml-2">
                    {toNumber(alert.usage_percent)}%
                  </span>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-gray-400">
                <CheckCircle2 size={48} className="mx-auto mb-2 opacity-50" />
                <p>Nenhum alerta de limite</p>
                <p className="text-xs mt-1">Todas as empresas estão dentro dos limites</p>
              </div>
            )}
          </div>
        </div>

        {/* Empresas Suspensas */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-50">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <XCircle size={20} className="text-red-500" />
              Empresas Suspensas
            </h2>
          </div>
          <div className="divide-y divide-gray-50 max-h-80 overflow-y-auto">
            {metrics.delinquentTenants && metrics.delinquentTenants.length > 0 ? (
              metrics.delinquentTenants.slice(0, 5).map((tenant, idx) => (
                <div key={idx} className="p-4 hover:bg-gray-50">
                  <p className="font-medium text-gray-900">{tenant.name}</p>
                  <p className="text-sm text-gray-500">{tenant.responsible_email}</p>
                  {tenant.suspended_reason && (
                    <p className="text-xs text-red-600 mt-1 bg-red-50 p-2 rounded">
                      <strong>Motivo:</strong> {tenant.suspended_reason}
                    </p>
                  )}
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-gray-400">
                <CheckCircle2 size={48} className="mx-auto mb-2 opacity-50" />
                <p>Nenhuma empresa suspensa</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top Empresas por Uso */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-50">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Activity size={20} className="text-blue-500" />
            Top 10 Empresas por Uso de Mensagens
          </h2>
          <p className="text-sm text-gray-500 mt-1">Últimos 30 dias</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="px-6 py-3 font-semibold">#</th>
                <th className="px-6 py-3 font-semibold">Empresa</th>
                <th className="px-6 py-3 font-semibold">Plano</th>
                <th className="px-6 py-3 font-semibold">Mensagens</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {metrics.topTenants && metrics.topTenants.length > 0 ? (
                metrics.topTenants.map((tenant, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-500 font-bold">
                      {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}
                    </td>
                    <td className="px-6 py-4 font-medium text-gray-900">{tenant.name}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-bold uppercase">
                        {tenant.plan_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-semibold">
                      {toNumber(tenant.message_count).toLocaleString('pt-BR')}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-gray-400">
                    <MessageSquare size={32} className="mx-auto mb-2 opacity-50" />
                    <p>Sem dados de uso ainda</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ações Rápidas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <button 
          onClick={() => window.location.href = '/master-admin/tenants'}
          className="p-6 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-2xl shadow-lg hover:shadow-xl transition-all text-left group">
          <Building2 size={32} className="mb-3 group-hover:scale-110 transition-transform" />
          <h3 className="font-bold text-lg mb-1">Gerenciar Empresas</h3>
          <p className="text-sm opacity-90">Ver todas as empresas e ações</p>
        </button>

        <button 
          onClick={() => window.location.href = '/reports'}
          className="p-6 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-2xl shadow-lg hover:shadow-xl transition-all text-left group">
          <BarChart3 size={32} className="mb-3 group-hover:scale-110 transition-transform" />
          <h3 className="font-bold text-lg mb-1">Relatórios</h3>
          <p className="text-sm opacity-90">Análises detalhadas do sistema</p>
        </button>

        <button 
          onClick={() => window.location.href = '/tenants'}
          className="p-6 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-2xl shadow-lg hover:shadow-xl transition-all text-left group">
          <Activity size={32} className="mb-3 group-hover:scale-110 transition-transform" />
          <h3 className="font-bold text-lg mb-1">Configurações</h3>
          <p className="text-sm opacity-90">Ajustes globais do sistema</p>
        </button>
      </div>
    </div>
  );
}

export default MasterDashboard;